import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { LoadingState, ErrorState } from "@/components/ui/AsyncState";
import { LeadStatusBadge, QualificationBadge, LEAD_STATUS_OPTIONS, CallOutcomeBadge, ConversationStatusBadge } from "@/components/ui/StatusBadge";
import { useApi } from "@/lib/use-api";
import { useAuth } from "@/lib/auth-context";
import { apiFetch, ApiRequestError } from "@/lib/api-client";
import { formatCurrencyFromCents, formatPhone, formatDateTime, formatRelativeTime } from "@/lib/format";
import type { Lead, LeadStatus, CallWithLead, ConversationWithPreview } from "@platform/shared";

const SOURCE_LABEL: Record<Lead["source"], string> = {
  missed_call: "Missed call",
  manual: "Manual",
  other: "Other",
};

const REASON_LABEL: Record<string, string> = {
  ai_requested_human: "The AI asked for a person to take over.",
  ai_output_unusable: "The AI's answer couldn't be used, so nothing was guessed.",
  rule_violation_conflicts_with_ai: "The AI's view conflicts with one of your qualification rules.",
  unqualified_without_rule_basis: "The AI thought this wasn't a fit, but none of your rules confirm it.",
  missing_required_fields: "Still missing details your rules require.",
  rules_confirm_unqualified: "The AI and your rules both say this isn't a fit.",
  rules_and_ai_confirm_qualified: "The AI and your rules both say this is a fit.",
  information_incomplete: "The AI needs more information.",
};

interface QualificationDataView {
  reasons?: string[];
  missingFields?: string[];
  ruleViolations?: string[];
  aiReasoning?: string | null;
  conflicts?: Record<string, { existing: string; suggested: string }>;
}

function asQualificationView(value: unknown): QualificationDataView | null {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as QualificationDataView) : null;
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-line py-2.5 text-sm last:border-0">
      <span className="text-ink-soft">{label}</span>
      <span className="text-right font-medium text-ink">{value}</span>
    </div>
  );
}

export function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { organizations } = useAuth();
  const canEdit = ["owner", "admin"].includes(organizations[0]?.role ?? "");

  const leadState = useApi<{ lead: Lead }>(`/api/leads/${id}`, [id]);
  const callsState = useApi<{ calls: CallWithLead[] }>(`/api/calls?leadId=${id}`, [id]);
  const conversationsState = useApi<{ conversations: ConversationWithPreview[] }>(
    `/api/conversations?leadId=${id}`,
    [id]
  );

  const [savingStatus, setSavingStatus] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function handleStatusChange(next: LeadStatus) {
    if (!id) return;
    setSavingStatus(true);
    setStatusError(null);
    try {
      await apiFetch(`/api/leads/${id}`, { method: "PATCH", body: JSON.stringify({ status: next }) });
      leadState.refetch();
    } catch (err) {
      setStatusError(err instanceof ApiRequestError ? err.message : "Couldn't update status.");
    } finally {
      setSavingStatus(false);
    }
  }

  const [revenueInput, setRevenueInput] = useState("");

  async function handleConfirmRevenue() {
    if (!id) return;
    const dollars = Number(revenueInput.replace(/[$,\s]/g, ""));
    if (!Number.isFinite(dollars) || dollars < 0) {
      setStatusError("Enter the revenue as a dollar amount, e.g. 450 or 1250.50.");
      return;
    }
    setSavingStatus(true);
    setStatusError(null);
    try {
      await apiFetch(`/api/leads/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ confirmedRevenueCents: Math.round(dollars * 100) }),
      });
      setRevenueInput("");
      leadState.refetch();
    } catch (err) {
      setStatusError(err instanceof ApiRequestError ? err.message : "Couldn't save revenue.");
    } finally {
      setSavingStatus(false);
    }
  }

  async function handleMarkReviewed() {
    if (!id) return;
    setSavingStatus(true);
    setStatusError(null);
    try {
      await apiFetch(`/api/leads/${id}`, { method: "PATCH", body: JSON.stringify({ needsHuman: false }) });
      leadState.refetch();
    } catch (err) {
      setStatusError(err instanceof ApiRequestError ? err.message : "Couldn't update lead.");
    } finally {
      setSavingStatus(false);
    }
  }

  async function handleDelete() {
    if (!id) return;
    if (!window.confirm("Delete this lead? This can't be undone.")) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiFetch(`/api/leads/${id}`, { method: "DELETE" });
      navigate("/dashboard/leads");
    } catch (err) {
      setDeleteError(err instanceof ApiRequestError ? err.message : "Couldn't delete lead.");
      setDeleting(false);
    }
  }

  if (leadState.isLoading) return <LoadingState label="Loading lead…" />;
  if (leadState.error) return <ErrorState message={leadState.error} onRetry={leadState.refetch} />;
  if (!leadState.data) return null;

  const lead = leadState.data.lead;
  const qualification = asQualificationView(lead.qualificationData);

  return (
    <div className="space-y-6">
      <div>
        <Link to="/dashboard/leads" className="text-sm text-ink-soft hover:text-ink">
          ← Back to leads
        </Link>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl text-ink">{lead.name ?? "Unknown caller"}</h1>
            <p className="mt-1 text-sm text-ink-soft">{formatPhone(lead.phone)}</p>
          </div>
          <div className="flex items-center gap-2">
            <LeadStatusBadge status={lead.status} />
            {canEdit && (
              <select
                value={lead.status}
                disabled={savingStatus}
                onChange={(e) => handleStatusChange(e.target.value as LeadStatus)}
                className="rounded-md border border-line bg-paper-raised px-2 py-1.5 text-sm text-ink disabled:opacity-50"
              >
                {LEAD_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    Move to: {opt.label}
                  </option>
                ))}
              </select>
            )}
            {canEdit && (
              <Button variant="secondary" onClick={handleDelete} disabled={deleting}>
                {deleting ? "Deleting…" : "Delete lead"}
              </Button>
            )}
          </div>
        </div>
        {statusError && <p className="mt-2 text-sm text-signal-600">{statusError}</p>}
        {deleteError && <p className="mt-2 text-sm text-signal-600">{deleteError}</p>}
      </div>

      {lead.needsHuman && (
        <Card className="border-signal-300 bg-signal-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-medium text-signal-600">This lead needs a person</p>
              <p className="mt-0.5 text-sm text-ink-soft">
                {qualification?.reasons?.map((r) => REASON_LABEL[r] ?? r).join(" ") ||
                  "Automation flagged this lead instead of guessing."}
              </p>
            </div>
            <div className="flex gap-2">
              {lead.conversationId && (
                <Link to={`/dashboard/conversations/${lead.conversationId}`}>
                  <Button variant="secondary">Open conversation</Button>
                </Link>
              )}
              {canEdit && (
                <Button onClick={handleMarkReviewed} disabled={savingStatus}>
                  Mark as reviewed
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-[1.2fr,1fr]">
        <div className="space-y-6">
          <Card className="p-5">
            <p className="mb-1 font-medium text-ink">Lead details</p>
            <div className="flex items-center justify-between gap-4 border-b border-line py-2.5 text-sm">
              <span className="text-ink-soft">Qualification</span>
              <QualificationBadge decision={lead.qualificationStatus} />
            </div>
            <DetailRow label="Service" value={lead.service ?? "—"} />
            <DetailRow label="Email" value={lead.email ?? "—"} />
            <DetailRow label="Address" value={lead.address ?? "—"} />
            <DetailRow label="Timeline" value={lead.timeline ?? "—"} />
            <DetailRow label="Estimated value (a guess)" value={formatCurrencyFromCents(lead.estimatedValueCents)} />
            {lead.status === "won" && (
              <div className="border-b border-line py-2.5 text-sm">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-ink-soft">Confirmed revenue (actual)</span>
                  <span className="font-medium text-ink">
                    {lead.confirmedRevenueCents === null ? "Not recorded" : formatCurrencyFromCents(lead.confirmedRevenueCents)}
                  </span>
                </div>
                {canEdit && (
                  <div className="mt-2 flex gap-2">
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="Amount received, e.g. 450"
                      value={revenueInput}
                      onChange={(e) => setRevenueInput(e.target.value)}
                      className="w-full rounded-md border border-line bg-paper-raised px-2 py-1.5 text-sm text-ink"
                    />
                    <Button variant="secondary" onClick={handleConfirmRevenue} disabled={savingStatus || !revenueInput.trim()}>
                      {lead.confirmedRevenueCents === null ? "Confirm" : "Update"}
                    </Button>
                  </div>
                )}
              </div>
            )}
            <DetailRow label="Source" value={SOURCE_LABEL[lead.source]} />
            <DetailRow label="Needs human follow-up" value={lead.needsHuman ? "Yes" : "No"} />
            <DetailRow label="Created" value={formatDateTime(lead.createdAt)} />
            <DetailRow label="Last updated" value={formatDateTime(lead.updatedAt)} />
          </Card>

          {qualification && (qualification.missingFields?.length || qualification.ruleViolations?.length || qualification.aiReasoning || (qualification.conflicts && Object.keys(qualification.conflicts).length > 0)) && (
            <Card className="p-5">
              <p className="mb-1 font-medium text-ink">Why this qualification</p>
              {qualification.aiReasoning && <DetailRow label="AI reasoning" value={qualification.aiReasoning} />}
              {!!qualification.missingFields?.length && (
                <DetailRow label="Still needed" value={qualification.missingFields.join(", ")} />
              )}
              {!!qualification.ruleViolations?.length && (
                <DetailRow label="Rule violations" value={qualification.ruleViolations.join(", ")} />
              )}
              {qualification.conflicts &&
                Object.entries(qualification.conflicts).map(([field, c]) => (
                  <DetailRow key={field} label={`${field}: customer may have said`} value={`${c.suggested} (kept: ${c.existing})`} />
                ))}
            </Card>
          )}

          {lead.qualificationData !== null && lead.qualificationData !== undefined && (
            <Card className="p-5">
              <p className="mb-2 font-medium text-ink">Qualification data</p>
              <pre className="overflow-x-auto rounded-md bg-paper p-3 text-xs text-ink-soft">
                {JSON.stringify(lead.qualificationData, null, 2)}
              </pre>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card className="overflow-hidden">
            <div className="border-b border-line px-5 py-3">
              <p className="font-medium text-ink">Calls</p>
            </div>
            {callsState.isLoading && <LoadingState />}
            {callsState.data && callsState.data.calls.length === 0 && (
              <p className="px-5 py-4 text-sm text-ink-soft">No calls linked to this lead.</p>
            )}
            {callsState.data && callsState.data.calls.length > 0 && (
              <ul className="divide-y divide-line">
                {callsState.data.calls.map((call) => (
                  <li key={call.id} className="flex items-center justify-between px-5 py-3 text-sm">
                    <span className="text-ink-soft">{formatDateTime(call.occurredAt)}</span>
                    <CallOutcomeBadge outcome={call.outcome} />
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="overflow-hidden">
            <div className="border-b border-line px-5 py-3">
              <p className="font-medium text-ink">Conversations</p>
            </div>
            {conversationsState.isLoading && <LoadingState />}
            {conversationsState.data && conversationsState.data.conversations.length === 0 && (
              <p className="px-5 py-4 text-sm text-ink-soft">No conversations linked to this lead.</p>
            )}
            {conversationsState.data && conversationsState.data.conversations.length > 0 && (
              <ul className="divide-y divide-line">
                {conversationsState.data.conversations.map((conv) => (
                  <li key={conv.id} className="px-5 py-3 text-sm">
                    <Link to={`/dashboard/conversations/${conv.id}`} className="block hover:underline">
                      <div className="flex items-center justify-between">
                        <span className="text-ink-soft">{formatRelativeTime(conv.updatedAt)}</span>
                        <ConversationStatusBadge status={conv.status} />
                      </div>
                      {conv.lastMessage && <p className="mt-1 truncate text-ink-soft">{conv.lastMessage}</p>}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
